import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Star, MapPin, Languages, Briefcase, MessageCircle, Loader2, Mail, Phone, User, Send, IndianRupee } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { fetchLawyers, fetchSpecialties, contactLawyer } from '../lib/api';

const parseFee = (feeStr = '') => {
  const digits = feeStr.replace(/[^0-9]/g, '');
  return digits ? parseInt(digits, 10) : 0;
};

const initials = (name = '') =>
  name
    .replace(/^Adv\.\s*/i, '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('');

const FindLawyer = () => {
  const [query, setQuery] = useState('');
  const [specialty, setSpecialty] = useState('All');
  const [lawyers, setLawyers] = useState([]);
  const [specialties, setSpecialties] = useState(['All']);
  const [loading, setLoading] = useState(true);
  const [activeLawyer, setActiveLawyer] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', note: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSpecialties().then(setSpecialties).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (specialty && specialty !== 'All') params.specialty = specialty;
    if (query.trim()) params.q = query.trim();
    const t = setTimeout(() => {
      fetchLawyers(params)
        .then((data) => setLawyers(data))
        .catch(() => toast.error('Could not load lawyers. Please retry.'))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query, specialty]);

  const total = useMemo(() => lawyers.length, [lawyers]);

  const openContact = (lawyer) => {
    setActiveLawyer(lawyer);
    setForm({ name: '', phone: '', email: '', note: '' });
  };

  const closeContact = () => {
    if (submitting) return;
    setActiveLawyer(null);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!activeLawyer) return;
    if (!form.name.trim() || !form.phone.trim() || !form.note.trim()) {
      toast.error('Please share your name, phone, and a short message.');
      return;
    }
    setSubmitting(true);
    try {
      await contactLawyer({
        lawyer_id: activeLawyer.id,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        note: form.note.trim(),
      });
      toast.success(`Request sent for ${activeLawyer.name}`, {
        description: 'Our coordinator team will get in touch with you shortly to arrange the introduction.',
      });
      setActiveLawyer(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="bg-[#0F1B2D] text-[#F8F2E4] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.3em] text-[#E89530] mb-5">DIRECTORY</p>
          <h1 className="font-serif-besley text-5xl md:text-6xl mb-5">Find a verified advocate</h1>
          <p className="text-[#C9BFA8] text-lg max-w-2xl">
            Every profile is verified against the State Bar Council enrolment register. Reach out directly via phone, email, or the contact form on each card.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F2E4] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row gap-4 bg-white rounded-2xl p-5 shadow-sm border border-[#E8DCC0]">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, city or specialty…"
                className="pl-11 h-12 border-[#E8DCC0] focus-visible:ring-[#2D4373]"
              />
            </div>
            <Select value={specialty} onValueChange={setSpecialty}>
              <SelectTrigger className="md:w-72 h-12 border-[#E8DCC0]">
                <SelectValue placeholder="All specialties" />
              </SelectTrigger>
              <SelectContent>
                {specialties.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-sm text-[#5a5a5a] mt-5">
            {loading ? 'Loading…' : <>Showing <span className="font-medium text-[#0F1B2D]">{total}</span> advocate{total === 1 ? '' : 's'}</>}
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-24 text-[#5a5a5a]">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {lawyers.map((l) => (
                <article key={l.id} className="pillar-card bg-[#EDE3CC] rounded-2xl p-7 flex flex-col">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 rounded-full bg-[#0F1B2D] text-[#E89530] flex items-center justify-center font-serif-besley text-xl">
                      {initials(l.name)}
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-[#E89530] text-[#E89530]" />
                      <span className="text-sm font-medium text-[#0F1B2D]">{l.rating}</span>
                    </div>
                  </div>
                  <h3 className="font-serif-besley text-2xl text-[#0F1B2D] mb-1">
                    <Link to={`/lawyer/${l.id}`} className="hover:text-[#A36B1F] transition-colors" data-testid={`lawyer-card-name-${l.id}`}>
                      {l.name}
                    </Link>
                  </h3>
                  <p className="text-sm text-[#5a5a5a] mb-4">{l.specialty}</p>

                  {l.bio ? (
                    <p className="text-[13px] text-[#3a3a3a] leading-relaxed mb-4 line-clamp-3">{l.bio}</p>
                  ) : null}

                  <ul className="space-y-2 text-sm text-[#3a3a3a] mb-5">
                    <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#A36B1F]" /> {l.city}</li>
                    <li className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-[#A36B1F]" /> {l.experience} experience</li>
                    <li className="flex items-center gap-2"><Languages className="w-4 h-4 text-[#A36B1F]" /> {l.languages?.join(', ')}</li>
                  </ul>

                  {(l.phone || l.email) && null}

                  <div className="mt-auto flex flex-wrap items-center justify-between gap-3">
                    <Badge variant="secondary" className="bg-white text-[#0F1B2D] border border-[#D6CAB0] max-w-[45%] truncate">
                      {l.fee}
                    </Badge>
                    <div className="flex items-center gap-2 shrink-0">
                      <Link
                        to={`/lawyer/${l.id}`}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-[#0F1B2D]/30 text-[#0F1B2D] hover:bg-white transition-colors text-sm whitespace-nowrap"
                        data-testid={`view-profile-btn-${l.id}`}
                      >
                        View profile
                      </Link>
                      <button
                        onClick={() => openContact(l)}
                        className="btn-orange inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap"
                      >
                        <MessageCircle className="w-4 h-4" /> Contact
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {!loading && lawyers.length === 0 && (
            <div className="text-center py-20 text-[#5a5a5a]">
              No advocates match your search. Try a different specialty or city.
            </div>
          )}
        </div>
      </section>

      <Dialog open={!!activeLawyer} onOpenChange={(open) => { if (!open) closeContact(); }}>
        <DialogContent className="sm:max-w-lg bg-[#F8F2E4] border border-[#E8DCC0]">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-[#0F1B2D] text-[#E89530] flex items-center justify-center font-serif-besley text-lg">
                {activeLawyer ? initials(activeLawyer.name) : ''}
              </div>
              <div>
                <DialogTitle className="font-serif-besley text-xl text-[#0F1B2D]">
                  Contact {activeLawyer?.name}
                </DialogTitle>
                <DialogDescription className="text-[#5a5a5a] text-sm">
                  {activeLawyer?.specialty} · {activeLawyer?.city}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <form onSubmit={handleSend} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#0F1B2D]">Your name *</Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="pl-10 h-11 border-[#E8DCC0] bg-white" />
                </div>
              </div>
              <div>
                <Label className="text-[#0F1B2D]">Phone *</Label>
                <div className="relative mt-1.5">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="pl-10 h-11 border-[#E8DCC0] bg-white" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-[#0F1B2D]">Email (optional)</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="pl-10 h-11 border-[#E8DCC0] bg-white" />
              </div>
            </div>
            <div>
              <Label className="text-[#0F1B2D]">Your message *</Label>
              <Textarea
                rows={4}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Briefly describe your matter and preferred time for a callback."
                className="mt-1.5 border-[#E8DCC0] bg-white"
              />
            </div>
            <p className="text-xs text-[#5a5a5a]">
              Your details will be shared only with {activeLawyer?.name} and our coordinator team. We never post them publicly.
            </p>
            {activeLawyer && parseFee(activeLawyer.fee) > 0 && (
              <div className="bg-[#EDE3CC] border border-[#D6CAB0] rounded-lg p-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs tracking-[0.2em] text-[#A36B1F] mb-1">PAID CONSULTATION</p>
                  <p className="text-sm text-[#3a3a3a]">Book an initial consultation for <span className="font-serif-besley text-lg text-[#0F1B2D] inline-flex items-center align-middle"><IndianRupee className="w-3.5 h-3.5" />{parseFee(activeLawyer.fee).toLocaleString('en-IN')}</span></p>
                </div>
                <Link
                  to={`/shop/checkout?type=consultation&id=${activeLawyer.id}`}
                  className="btn-orange inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium shrink-0"
                >
                  Pay &amp; book
                </Link>
              </div>
            )}
            <div className="flex items-center justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={closeContact}
                disabled={submitting}
                className="px-4 py-2 rounded-md border border-[#D6CAB0] text-sm text-[#0F1B2D] bg-white hover:bg-[#EDE3CC] transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn-orange inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium disabled:opacity-70"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitting ? 'Sending…' : 'Send message'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FindLawyer;
